import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { backButton, onBackButtonClick } from '@telegram-apps/sdk';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, ReportStats } from '../lib/api';
import { hapticFeedback } from '../lib/telegram';
import './ReportPage.css';

export function ReportPage() {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [lastStats, setLastStats] = useState<ReportStats | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Get available periods
  const { data: periods, isLoading, isError } = useQuery({
    queryKey: ['report-periods'],
    queryFn: () => api.getReportPeriods(),
  });

  // Send report mutation
  const sendMutation = useMutation({
    mutationFn: async ({ year, month }: { year: number; month: number }) => {
      return api.sendReport(year, month);
    },
    onSuccess: ({ stats }) => {
      if (stats) {
        setLastStats(stats);
      }
      setShowSuccess(true);
      hapticFeedback('success');
      setTimeout(() => setShowSuccess(false), 3000);
    },
    onError: (error) => {
      hapticFeedback('error');
      console.error('Report send failed:', error);
    },
  });

  // Initialize with current period
  useEffect(() => {
    if (periods && !selectedYear) {
      setSelectedYear(periods.currentYear);
      setSelectedMonth(periods.currentMonth);
    }
  }, [periods, selectedYear]);

  // Setup back button
  useEffect(() => {
    if (backButton.show.isAvailable()) {
      backButton.show();
    }

    let removeListener: (() => void) | undefined;
    if (onBackButtonClick.isAvailable()) {
      removeListener = onBackButtonClick(() => {
        hapticFeedback('light');
        navigate(-1);
      });
    }

    return () => {
      if (backButton.hide.isAvailable()) {
        backButton.hide();
      }
      if (removeListener) {
        removeListener();
      }
    };
  }, [navigate]);

  const handleSend = () => {
    if (!selectedYear || !selectedMonth) return;
    hapticFeedback('medium');
    sendMutation.mutate({ year: selectedYear, month: selectedMonth });
  };

  const isMonthAvailable = (year: number, month: number): boolean => {
    if (!periods) return false;
    return year < periods.currentYear ||
      (year === periods.currentYear && month <= periods.currentMonth);
  };

  if (isLoading) {
    return (
      <div className="report-page">
        <div className="report-loading">
          <div className="report-spinner" />
          <span className="report-loading-text">Загрузка...</span>
        </div>
      </div>
    );
  }

  if (isError || !periods) {
    return (
      <div className="report-page">
        <div className="report-error">
          <div className="report-error-icon">⚠️</div>
          <h2 className="report-error-title">Ошибка</h2>
          <p className="report-error-text">Не удалось загрузить данные</p>
          <button
            type="button"
            className="report-back-btn"
            onClick={() => navigate(-1)}
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  const selectedMonthLabel = periods.months.find(m => m.value === selectedMonth)?.label;

  return (
    <div className="report-page">
      {/* Header */}
      <header className="report-header">
        <div className="report-header-icon">📊</div>
        <div className="report-header-content">
          <h1 className="report-title">Ежемесячный отчёт</h1>
          <p className="report-subtitle">Генерация Excel отчёта по заявкам</p>
        </div>
      </header>

      {/* Year Selection */}
      <section className="report-section">
        <div className="report-section-header">Год</div>
        <div className="report-year-grid">
          {periods.years.map((year, index) => (
            <button
              key={year}
              type="button"
              className={`report-year-btn ${selectedYear === year ? 'report-year-btn--active' : ''}`}
              style={{ animationDelay: `${index * 50}ms` }}
              onClick={() => {
                hapticFeedback('light');
                setSelectedYear(year);
                if (selectedMonth && !isMonthAvailable(year, selectedMonth)) {
                  setSelectedMonth(null);
                }
              }}
            >
              {year}
            </button>
          ))}
        </div>
      </section>

      {/* Month Selection */}
      {selectedYear && (
        <section className="report-section report-section--animated">
          <div className="report-section-header">Месяц</div>
          <div className="report-month-grid">
            {periods.months.map((m, index) => {
              const available = isMonthAvailable(selectedYear, m.value);
              return (
                <button
                  key={m.value}
                  type="button"
                  className={`report-month-btn ${selectedMonth === m.value ? 'report-month-btn--active' : ''} ${!available ? 'report-month-btn--disabled' : ''}`}
                  style={{ animationDelay: `${index * 30}ms` }}
                  disabled={!available}
                  onClick={() => {
                    if (available) {
                      hapticFeedback('light');
                      setSelectedMonth(m.value);
                    }
                  }}
                >
                  {m.label.slice(0, 3)}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Selected period preview */}
      {selectedYear && selectedMonth && (
        <div className="report-preview">
          <div className="report-preview-label">Выбранный период</div>
          <div className="report-preview-value">
            {selectedMonthLabel} {selectedYear}
          </div>
        </div>
      )}

      {/* Last download stats */}
      {lastStats && (
        <section className="report-section report-section--animated">
          <div className="report-section-header">Последний отчёт</div>
          <div className="report-stats-grid">
            <div className="report-stat-card">
              <div className="report-stat-value">{lastStats.total}</div>
              <div className="report-stat-label">Всего</div>
            </div>
            <div className="report-stat-card">
              <div className="report-stat-value report-stat-value--success">{lastStats.completed}</div>
              <div className="report-stat-label">Завершено</div>
            </div>
            <div className="report-stat-card">
              <div className="report-stat-value report-stat-value--info">{lastStats.created}</div>
              <div className="report-stat-label">Создано</div>
            </div>
            <div className="report-stat-card">
              <div className="report-stat-value report-stat-value--warning">{lastStats.received}</div>
              <div className="report-stat-label">Получено</div>
            </div>
          </div>
        </section>
      )}

      {/* Error message */}
      {sendMutation.isError && (
        <div className="report-error-message">
          <span className="report-error-message-icon">⚠️</span>
          <span className="report-error-message-text">
            {sendMutation.error instanceof Error
              ? sendMutation.error.message
              : 'Ошибка отправки отчёта'}
          </span>
        </div>
      )}

      {/* Send button */}
      <div className="report-actions">
        <button
          type="button"
          className="report-send-btn"
          disabled={!selectedYear || !selectedMonth || sendMutation.isPending}
          onClick={handleSend}
        >
          {sendMutation.isPending ? (
            <>
              <span className="report-btn-spinner" />
              Отправка...
            </>
          ) : (
            <>📊 Отправить отчёт в чат</>
          )}
        </button>
      </div>

      {/* Success toast */}
      {showSuccess && (
        <div className="report-toast">
          <div className="report-toast-content">
            <span className="report-toast-icon">✅</span>
            <span className="report-toast-text">Отчёт отправлен в чат с ботом</span>
          </div>
        </div>
      )}
    </div>
  );
}
